import type { SlotDto } from '@motoschool/shared';
import { formatMoney, SLOT_STATUS_LABELS } from '@motoschool/shared';
import { Avatar, Badge, cn } from './ui';
import { formatTime } from '../lib/format';

const STATUS_TONE = {
  OPEN: 'success',
  BOOKED: 'brand',
  COMPLETED: 'neutral',
  NO_SHOW: 'warning',
  CANCELLED: 'danger',
} as const;

interface SlotCardProps {
  slot: SlotDto;
  timeZone: string;
  onClick?: () => void;
  /** Ученик не видит имён других учеников, только занятость окна. */
  hideStudent?: boolean;
}

export function SlotCard({ slot, timeZone, onClick, hideStudent }: SlotCardProps): React.JSX.Element {
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base font-semibold tabular-nums">
            {formatTime(slot.startsAt, timeZone)} — {formatTime(slot.endsAt, timeZone)}
          </p>
          {slot.location && <p className="mt-0.5 truncate text-sm text-muted">{slot.location}</p>}
        </div>
        <Badge tone={STATUS_TONE[slot.status]}>{SLOT_STATUS_LABELS[slot.status]}</Badge>
      </div>

      {!hideStudent && slot.student && (
        <div className="mt-3 flex items-center gap-2">
          <Avatar name={slot.student.name} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{slot.student.name}</p>
            {slot.student.phone && <p className="text-xs text-muted">{slot.student.phone}</p>}
          </div>
        </div>
      )}

      {slot.note && <p className="mt-2 line-clamp-2 text-sm text-muted">{slot.note}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted">
        <span>{slot.durationMinutes} мин</span>
        {slot.priceMinor !== null && <span>· {formatMoney(slot.priceMinor)}</span>}
        {slot.pendingRequestCount > 0 && (
          <Badge tone="warning">
            {slot.pendingRequestCount} {slot.pendingRequestCount === 1 ? 'заявка' : 'заявки'}
          </Badge>
        )}
      </div>
    </>
  );

  if (!onClick) {
    return <div className="rounded-card border border-line bg-surface p-4">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-card border border-line bg-surface p-4 text-left transition-colors',
        'hover:border-brand-200 hover:bg-brand-50/30 focus-visible:outline-2 focus-visible:outline-brand-600',
      )}
    >
      {content}
    </button>
  );
}
